import type { Span } from "@sentry/react";
import * as Sentry from "@sentry/react";

type BrowserOptions = NonNullable<Parameters<typeof Sentry.init>[0]>;
/** beforeSendTransaction に渡る transaction イベント（@sentry/react は型を再公開していないため init の型から取る） */
export type TransactionEvent = Parameters<
  NonNullable<BrowserOptions["beforeSendTransaction"]>
>[0];

type Active = { span: Span | null };
let active: Active | null = null;

/**
 * 遷移 root span（navigation。起動は pageload）の子として ui.render スパンを T0 で開始する。
 * 子スパンが生きている間は root の idle 判定が待つので、root が描画前に閉じる問題も同時に防ぐ。
 * 起動（isInitial）も子を作る: redirect 連鎖の起動では Sentry は navigation span を作らず pageload が root のままなので、
 * 子が無いと主データを待つ間に pageload が idle で閉じ、app.start_ms が付かない。
 * Keychain 自動ログインのように起動中に navigate が走って root が替わるケースでは pageload の子が cancelled になり、
 * 送信前に labelCancelledRenderSpans が superseded を付ける。
 *
 * @param startedAt T0（performance.now() 基準・ms）。redirect 連鎖で引き継いだ値のことがある
 */
export const startScreenRenderSpan = (
  pathname: string,
  isInitial: boolean,
  startedAt: number,
): void => {
  // redirect 連鎖で前の span が開いたままなら閉じる
  if (active?.span) {
    active.span.setAttribute("screen.ready_status", "superseded");
    active.span.end();
  }
  const start = () =>
    Sentry.startInactiveSpan({
      name: `screen.render ${pathname}`,
      op: "ui.render",
      startTime: (performance.timeOrigin + startedAt) / 1000,
      attributes: { "screen.pathname": pathname, "screen.initial": isInitial },
    });
  const current = Sentry.getActiveSpan();
  const root = current ? Sentry.getRootSpan(current) : undefined;
  active = { span: root ? Sentry.withActiveSpan(root, start) : start() };
};

export type ScreenRenderSpanEnd = {
  pathname: string;
  status: string;
  renderMs: number | null;
  firstPaintMs: number | null;
  screenId: string | null;
  readyKind: "ready" | "first_paint" | null;
  isInitial: boolean;
};

export const endScreenRenderSpan = (r: ScreenRenderSpanEnd): void => {
  if (!active) return;
  const { span } = active;
  active = null;
  span?.setAttributes({
    "screen.id": r.screenId ?? "unknown",
    "screen.ready_status": r.status,
    "screen.ready_kind": r.readyKind ?? "none",
    ...(r.firstPaintMs != null
      ? { "screen.first_paint_ms": r.firstPaintMs }
      : {}),
  });
  span?.end();
  // 計測値は「いま生きている root」に付ける（起動は pageload → navigation と root が替わることがあるため）
  const current = Sentry.getActiveSpan();
  const root = current ? Sentry.getRootSpan(current) : undefined;
  if (!root) return;
  // 起動は pageload の名前が /login のままなので、どの画面で中身が描けたかを root に残す
  if (r.isInitial) {
    root.setAttributes({
      "app.start_screen": r.pathname,
      "app.start_status": r.status,
    });
  }
  if (r.renderMs != null) {
    Sentry.setMeasurement(
      r.isInitial ? "app.start_ms" : "screen.render_ms",
      r.renderMs,
      "millisecond",
      root,
    );
  }
};

/**
 * 送信直前の transaction で、こちらの確定より先に Sentry が root を閉じて cancelled になった ui.render に状態を付ける。
 * 次の遷移の navigation span は onBeforeNavigate で始まり、前の root とその子を cancelled で閉じる。
 * こちらの abandoned 確定はその後の onBeforeLoad なので属性が乗らない（Sentry.init の beforeSendTransaction から呼ぶ）。
 */
export const labelCancelledRenderSpans = (
  event: TransactionEvent,
): TransactionEvent => {
  const rootReason =
    event.contexts?.trace?.data?.["sentry.idle_span_finish_reason"];
  for (const span of event.spans ?? []) {
    if (span.op !== "ui.render" || span.status !== "cancelled") continue;
    const data = span.data ?? {};
    if (data["screen.ready_status"] != null) continue;
    data["screen.ready_status"] =
      data["screen.initial"] === true
        ? "superseded"
        : rootReason === "cancelled"
          ? "abandoned"
          : "timeout";
    data["screen.ready_kind"] = "none";
    span.data = data;
  }
  return event;
};

/** テスト用 */
export const resetScreenRenderSpanState = (): void => {
  active = null;
};
